import { addDays, successLevel, type LocalDate, type Progress } from '@planer/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, type PanInfo } from 'motion/react';
import { dayNumber, isoWeek, monthName, weekdayLetter } from '../lib/format';

const LEVEL_COLOR = {
  none: 'transparent',
  low: 'var(--missed)',
  mid: 'var(--mid)',
  high: 'var(--done)',
} as const;
const LEVEL_TEXT = { none: '', low: 'slabo', mid: 'delno', high: 'dobro' } as const;

interface WeekStripProps {
  days: LocalDate[];
  progress: Map<LocalDate, Progress>;
  today: LocalDate;
  selected: LocalDate;
  onSelect: (date: LocalDate) => void;
}

/** Seven days with success dots; swipe or arrows move by a week. */
export function WeekStrip({ days, progress, today, selected, onSelect }: WeekStripProps) {
  const first = days[0] ?? selected;
  const shift = (weeks: number) => {
    onSelect(addDays(selected, weeks * 7));
  };
  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.x < -50) shift(1);
    else if (info.offset.x > 50) shift(-1);
  };
  const last = days[days.length - 1] ?? first;
  const month =
    monthName(first) === monthName(last)
      ? monthName(first)
      : `${monthName(first).slice(0, 3)} – ${monthName(last).slice(0, 3)}`;

  return (
    <section aria-label="Teden" className="card px-2 pt-2 pb-2.5">
      <div className="flex items-center justify-between px-2">
        <p className="text-[13px] font-semibold text-muted">
          <span className="inline-block first-letter:uppercase">{month}</span>
          <span className="mx-1.5 text-muted">·</span>
          teden {isoWeek(first)}
        </p>
        <div className="flex">
          <button
            type="button"
            aria-label="Prejšnji teden"
            className="tap grid h-10 w-10 place-items-center rounded-full text-muted active:bg-surface-2"
            onClick={() => {
              shift(-1);
            }}
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            aria-label="Naslednji teden"
            className="tap grid h-10 w-10 place-items-center rounded-full text-muted active:bg-surface-2"
            onClick={() => {
              shift(1);
            }}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
      <motion.div
        key={first}
        className="grid grid-cols-7"
        drag="x"
        dragDirectionLock
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.25}
        onDragEnd={onDragEnd}
        style={{ touchAction: 'pan-y' }}
      >
        {days.map((d) => {
          const p = progress.get(d);
          const level = d > today ? 'none' : successLevel(p?.ratio ?? null);
          const isSelected = d === selected;
          const isToday = d === today;
          const label = `${dayNumber(d)}.${isToday ? ' danes' : ''}${
            p && p.total > 0 && d <= today ? `, ${p.done} od ${p.total} ${LEVEL_TEXT[level]}` : ''
          }`;
          return (
            <button
              key={d}
              type="button"
              onClick={() => {
                onSelect(d);
              }}
              aria-current={isSelected ? 'date' : undefined}
              aria-label={label}
              className="tap flex min-h-[4.25rem] flex-col items-center justify-center gap-1 rounded-2xl"
            >
              <span
                className={`text-[11px] font-semibold ${isToday ? 'text-accent' : 'text-muted'}`}
              >
                {weekdayLetter(d)}
              </span>
              <span
                className={`grid h-9 w-9 place-items-center rounded-full text-[15px] font-semibold tabular-nums transition-colors ${
                  isSelected
                    ? 'bg-accent text-on-accent'
                    : isToday
                      ? 'bg-accent-soft text-accent-ink'
                      : 'text-text'
                }`}
              >
                {dayNumber(d)}
              </span>
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: LEVEL_COLOR[level] }}
                aria-hidden
              />
            </button>
          );
        })}
      </motion.div>
    </section>
  );
}
